import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Input, Stack, HStack, Badge, Icon, Spinner, IconButton, SimpleGrid } from "@chakra-ui/react";
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Eye, ShieldCheck, X, ListChecks } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { TemplatePreview } from "@/components/TemplatePreview";
import { TemplateVisibility } from "@/components/TemplateVisibility";
import { Modal } from "@/components/Modal";
import { FIELD_TYPES, LEGACY_TYPES, TEMPLATE_TYPES, fieldTypeMeta, isDropdownType, newField, newSection } from "@/lib/templateFields";
import { inputStyle } from "@/pages/auth/AuthShell";
import { colors } from "@/theme/colors";

const Select = ({ value, onChange, options, testId, ...rest }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId}
    px="3" py="2" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm" {...rest}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </Box>
);

const CheckToggle = ({ checked, onChange, label, testId }) => (
  <HStack as="label" gap="1.5" cursor="pointer" flexShrink={0}>
    <Box as="input" type="checkbox" checked={!!checked} onChange={onChange} data-testid={testId}
      accentColor={colors.teal} w="14px" h="14px" />
    <Text fontSize="xs" color={colors.textSecondary}>{label}</Text>
  </HStack>
);

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// SiteDocs-style "Add Item" type picker.
function TypePickerModal({ onPick, onClose }) {
  return (
    <Modal open onClose={onClose} title="Add item — choose a type" testId="type-picker-modal">
      <SimpleGrid columns={{ base: 2, sm: 3 }} gap="2">
        {FIELD_TYPES.map((t) => (
          <Button key={t.value} onClick={() => onPick(t.value)} data-testid={`pick-type-${t.value}`}
            h="auto" py="3" px="2" flexDirection="column" gap="1.5" whiteSpace="normal"
            bg={colors.page}
            _hover={{ bg: colors.tealTint, borderColor: colors.teal }}>
            <Icon as={t.icon} boxSize="5" color={colors.teal} />
            <Text fontSize="xs" fontWeight="500" textAlign="center">{t.label}</Text>
          </Button>
        ))}
      </SimpleGrid>
    </Modal>
  );
}

function FieldEditor({ field, lists, onChange, onRemove, onMove, index, total }) {
  const meta = fieldTypeMeta(field.type);
  const isInfo = field.type === "info";
  const isLegacyChoice = field.type === "multiple_choice";
  const typeOptions = [
    ...FIELD_TYPES.map((t) => ({ value: t.value, label: t.label })),
    ...LEGACY_TYPES.filter((t) => t.value === field.type).map((t) => ({ value: t.value, label: t.label })),
  ];
  const listOptions = [
    { value: "", label: "— choose a custom list —" },
    ...lists.filter((l) => l.company_id === null).map((l) => ({ value: l.id, label: `${l.name} (Hakina)` })),
    ...lists.filter((l) => l.company_id !== null).map((l) => ({ value: l.id, label: l.name })),
  ];
  const setOpt = (i, v) => {
    const options = [...(field.options || [])];
    options[i] = v;
    onChange({ ...field, options });
  };

  return (
    <Box border="1px solid" borderColor={colors.border} borderRadius="10px" p="3" bg={colors.page} data-testid={`field-editor-${field.id}`}>
      <Flex gap="2" flexWrap="wrap" align="center">
        <Icon as={meta.icon} boxSize="4" color={colors.teal} flexShrink={0} />
        <Input {...inputStyle} flex="1" minW="160px" bg={colors.white}
          placeholder={isInfo ? "Info text shown to the worker" : "Question / label"}
          value={field.label} onChange={(e) => onChange({ ...field, label: e.target.value })} data-testid={`field-label-${field.id}`} />
        <Select value={field.type} options={typeOptions} testId={`field-type-${field.id}`}
          onChange={(e) => {
            const type = e.target.value;
            const next = { ...field, type };
            if (isDropdownType(type) && next.list_id === undefined) next.list_id = null;
            if (!isDropdownType(type)) delete next.list_id;
            if (type !== "multiple_choice") delete next.options;
            onChange(next);
          }} />
        {!isInfo && (
          <Button size="xs" variant={field.required ? "solid" : "outline"}
            bg={field.required ? colors.teal : "transparent"} color={field.required ? "white" : colors.textSecondary} onClick={() => onChange({ ...field, required: !field.required })}
            data-testid={`field-required-${field.id}`}>
            Required
          </Button>
        )}
        <HStack gap="0">
          <IconButton aria-label="Move up" size="xs" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`field-up-${field.id}`}><ChevronUp size={15} /></IconButton>
          <IconButton aria-label="Move down" size="xs" variant="ghost" disabled={index === total - 1} onClick={() => onMove(1)} data-testid={`field-down-${field.id}`}><ChevronDown size={15} /></IconButton>
          <IconButton aria-label="Remove field" size="xs" variant="ghost" color={colors.poppy} onClick={onRemove} data-testid={`field-remove-${field.id}`}><Trash2 size={15} /></IconButton>
        </HStack>
      </Flex>

      {isDropdownType(field.type) && (
        <HStack gap="2" mt="2" pl="6" flexWrap="wrap">
          <Icon as={ListChecks} boxSize="4" color={colors.textSecondary} />
          <Select value={field.list_id || ""} options={listOptions} testId={`field-list-${field.id}`}
            onChange={(e) => onChange({ ...field, list_id: e.target.value || null })} />
          {!field.list_id && <Text fontSize="xs" color={colors.poppy}>Pick a list — manage them in the Custom Lists tab.</Text>}
        </HStack>
      )}

      {isLegacyChoice && (
        <Stack gap="1.5" mt="2" pl="6">
          <Text fontSize="xs" color={colors.textSecondary}>Legacy inline options — consider switching to a Dropdown backed by a Custom List.</Text>
          {(field.options || []).map((o, i) => (
            <HStack key={`${field.id}-opt-${i}`} gap="2">
              <Box w="12px" h="12px" borderRadius="full" border="2px solid" borderColor={colors.border} flexShrink={0} />
              <Input {...inputStyle} size="sm" bg={colors.white} value={o} onChange={(e) => setOpt(i, e.target.value)} data-testid={`field-option-${field.id}-${i}`} />
              <IconButton aria-label="Remove option" size="xs" variant="ghost" color={colors.textSecondary}
                disabled={(field.options || []).length <= 2}
                onClick={() => onChange({ ...field, options: field.options.filter((_, j) => j !== i) })}
                data-testid={`option-remove-${field.id}-${i}`}><X size={13} /></IconButton>
            </HStack>
          ))}
          <Button size="xs" alignSelf="flex-start"
            onClick={() => onChange({ ...field, options: [...(field.options || []), `Option ${(field.options || []).length + 1}`] })}
            data-testid={`add-option-${field.id}`}>
            <Plus size={13} /> Add option
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // section index awaiting a type pick

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    (async () => {
      const [{ data, error }, { data: sl }] = await Promise.all([
        supabase.from("templates").select("*").eq("id", id).maybeSingle(),
        supabase.from("select_lists").select("*").order("name", { ascending: true }),
      ]);
      setLists(sl || []);
      if (error || !data) { setTpl(null); setLoading(false); return; }
      if (!data.schema?.sections) data.schema = { sections: [] };
      setTpl(data);
      setLoading(false);
    })();
  }, [id]);

  const canEdit = Boolean(tpl) && isSuperAdmin;

  const update = useCallback((patch) => { setTpl((t) => ({ ...t, ...patch })); setDirty(true); }, []);
  const setSections = (sections) => update({ schema: { ...tpl.schema, sections } });

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("templates")
        .update({ title: tpl.title, type: tpl.type, schema: tpl.schema, version: tpl.version + 1 })
        .eq("id", tpl.id);
      if (error) throw error;
      setTpl((t) => ({ ...t, version: t.version + 1 }));
      setDirty(false);
      flash("success", "Template saved.");
    } catch (e) { flash("error", e.message); } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    const next = tpl.status === "published" ? "draft" : "published";
    const { error } = await supabase.from("templates").update({ status: next }).eq("id", tpl.id);
    if (error) flash("error", error.message);
    else { setTpl((t) => ({ ...t, status: next })); flash("success", next === "published" ? "Template published." : "Template moved to draft."); }
  };

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  if (!tpl) {
    return (
      <Box data-testid="template-editor-notfound">
        <Text color={colors.textSecondary}>Template not found or you don't have access to it.</Text>
        <Button mt="4" size="sm" variant="outline" onClick={() => navigate("/templates")}><ArrowLeft size={15} /> Back to templates</Button>
      </Box>
    );
  }

  const isMaster = tpl.company_id === null;
  const sections = tpl.schema.sections;

  return (
    <Box data-testid="template-editor-page" maxW="860px">
      <Button size="xs" variant="ghost" mb="3" onClick={() => navigate("/templates")} data-testid="editor-back-btn">
        <ArrowLeft size={14} /> All templates
      </Button>

      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap" mb="4">
        <Box>
          <HStack gap="2" flexWrap="wrap">
            <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "xl", md: "2xl" }} color={colors.textPrimary}>
              {canEdit ? "Edit template" : "View template"}
            </Heading>
            {isMaster && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5"><Icon as={ShieldCheck} boxSize="3" mr="1" />Master</Badge>}
            <Badge bg={tpl.status === "published" ? colors.resolvedBg : colors.pendingBg}
              color={tpl.status === "published" ? colors.resolvedText : colors.pendingText}
              borderRadius="full" px="2.5" textTransform="capitalize">{tpl.status}</Badge>
            <Text fontSize="xs" color={colors.textSecondary}>v{tpl.version}</Text>
          </HStack>
          {!canEdit && (
            <Text fontSize="sm" color={colors.textSecondary} mt="1" data-testid="readonly-note">
              Templates are managed by Hakina.
            </Text>
          )}
          {canEdit && !isMaster && (
            <Text fontSize="sm" color={colors.textSecondary} mt="1" data-testid="legacy-copy-note">
              Legacy client copy — hidden from all client users. Convert it to a Specialized template from the templates list to hand it back.
            </Text>
          )}
        </Box>
        <HStack gap="2">
          <Button size="sm" variant="outline" onClick={() => setPreview(true)} data-testid="editor-preview-btn"><Eye size={15} /> Preview</Button>
          {canEdit && (
            <>
              <Button size="sm" variant={tpl.status === "published" ? "outline" : "solid"}
                onClick={togglePublish} data-testid="editor-publish-btn">
                {tpl.status === "published" ? "Unpublish" : "Publish"}
              </Button>
              <Button size="sm"
                onClick={save} loading={saving} disabled={!dirty || !tpl.title} data-testid="editor-save-btn">
                Save changes
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {msg && (
        <Box mb="4" px="4" py="2.5" borderRadius="10px" fontSize="sm"
          bg={msg.type === "error" ? colors.criticalBg : colors.resolvedBg}
          color={msg.type === "error" ? colors.criticalText : colors.resolvedText}
          data-testid="editor-banner">{msg.text}</Box>
      )}

      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" mb="4">
        <Flex gap="3" flexWrap="wrap">
          <Box flex="1" minW="220px">
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Template title</Text>
            <Input {...inputStyle} value={tpl.title} onChange={(e) => update({ title: e.target.value })} disabled={!canEdit} data-testid="template-title-input" />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Type</Text>
            <Select value={tpl.type} options={TEMPLATE_TYPES.map((t) => ({ value: t, label: t }))}
              onChange={(e) => update({ type: e.target.value })} disabled={!canEdit} testId="template-type-select" />
          </Box>
        </Flex>
        <Text fontSize="xs" color={colors.textSecondary} mt="3">
          Location and submitting employee are captured automatically when a form is submitted — don't add them as fields.
        </Text>
      </Box>

      {canEdit && isMaster && <TemplateVisibility templateId={tpl.id} scope={tpl.scope} flash={flash} />}

      <Stack gap="4">
        {sections.map((sec, si) => (
          <Box key={sec.id} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid={`section-card-${sec.id}`}>
            <Flex gap="2" align="center" mb="2" flexWrap="wrap">
              <Input {...inputStyle} fontWeight="600" flex="1" minW="180px" placeholder={`Section ${si + 1} title (e.g., Hazards)`}
                value={sec.title} disabled={!canEdit}
                onChange={(e) => setSections(sections.map((s, i) => (i === si ? { ...s, title: e.target.value } : s)))}
                data-testid={`section-title-${sec.id}`} />
              {canEdit && (
                <HStack gap="0">
                  <IconButton aria-label="Move section up" size="xs" variant="ghost" disabled={si === 0} onClick={() => setSections(move(sections, si, -1))} data-testid={`section-up-${sec.id}`}><ChevronUp size={15} /></IconButton>
                  <IconButton aria-label="Move section down" size="xs" variant="ghost" disabled={si === sections.length - 1} onClick={() => setSections(move(sections, si, 1))} data-testid={`section-down-${sec.id}`}><ChevronDown size={15} /></IconButton>
                  <IconButton aria-label="Remove section" size="xs" variant="ghost" color={colors.poppy}
                    onClick={() => window.confirm("Remove this section and all its fields?") && setSections(sections.filter((_, i) => i !== si))}
                    data-testid={`section-remove-${sec.id}`}><Trash2 size={15} /></IconButton>
                </HStack>
              )}
            </Flex>
            <HStack gap="4" mb="3" pl="1">
              {canEdit ? (
                <>
                  <CheckToggle checked={sec.collapsible} label="Collapsible" testId={`section-collapsible-${sec.id}`}
                    onChange={(e) => setSections(sections.map((s, i) => (i === si ? { ...s, collapsible: e.target.checked } : s)))} />
                  <CheckToggle checked={sec.repeatable} label="Repeatable" testId={`section-repeatable-${sec.id}`}
                    onChange={(e) => setSections(sections.map((s, i) => (i === si ? { ...s, repeatable: e.target.checked } : s)))} />
                </>
              ) : (
                <>
                  {sec.collapsible && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">Collapsible</Badge>}
                  {sec.repeatable && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">Repeatable</Badge>}
                </>
              )}
            </HStack>
            <Stack gap="2.5">
              {sec.fields.map((fl, fi) => canEdit ? (
                <FieldEditor key={fl.id} field={fl} index={fi} total={sec.fields.length} lists={lists}
                  onChange={(nf) => setSections(sections.map((s, i) => (i === si ? { ...s, fields: s.fields.map((f, j) => (j === fi ? nf : f)) } : s)))}
                  onRemove={() => setSections(sections.map((s, i) => (i === si ? { ...s, fields: s.fields.filter((_, j) => j !== fi) } : s)))}
                  onMove={(dir) => setSections(sections.map((s, i) => (i === si ? { ...s, fields: move(s.fields, fi, dir) } : s)))}
                />
              ) : (
                <HStack key={fl.id} gap="2" px="3" py="2" borderRadius="8px" bg={colors.page} border="1px solid" borderColor={colors.border}>
                  <Icon as={fieldTypeMeta(fl.type).icon} boxSize="4" color={colors.teal} />
                  <Text fontSize="sm" color={colors.textPrimary}>{fl.label}</Text>
                  {fl.required && <Text color={colors.poppy} fontSize="sm">*</Text>}
                  <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px">
                    {fieldTypeMeta(fl.type).label}
                  </Badge>
                </HStack>
              ))}
              {canEdit && (
                <Button size="xs" alignSelf="flex-start"
                  onClick={() => setPickerFor(si)} data-testid={`add-field-${sec.id}`}>
                  <Plus size={14} /> Add item
                </Button>
              )}
            </Stack>
          </Box>
        ))}
        {canEdit && (
          <Button size="sm" alignSelf="flex-start"
            onClick={() => setSections([...sections, newSection()])} data-testid="add-section-btn">
            <Plus size={15} /> Add section
          </Button>
        )}
      </Stack>

      {pickerFor !== null && (
        <TypePickerModal
          onClose={() => setPickerFor(null)}
          onPick={(type) => {
            setSections(sections.map((s, i) => (i === pickerFor ? { ...s, fields: [...s.fields, newField(type)] } : s)));
            setPickerFor(null);
          }}
        />
      )}

      {preview && <TemplatePreview template={tpl} lists={lists} onClose={() => setPreview(false)} />}
    </Box>
  );
}
